import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "./Select.module.css";

/**
 * Premium single-select dropdown.
 *
 * Contract is identical to a native <select>: caller passes a string `value`
 * and gets the new value back via `onChange(value)`. No state is lifted out
 * of the parent — the cascade reset logic in the staff modal stays put.
 *
 * Why custom: native <select> can't be styled to match the rest of the app,
 * has no typeahead UI, and gives the OS-default open animation. This one
 * borrows the DatePicker portal/popover pattern so the popup can never be
 * clipped by a modal's overflow and survives scroll/resize without drift.
 *
 * Accessibility: WAI-ARIA listbox combobox. Trigger is role="combobox",
 * popup is role="listbox", each row is role="option". Full keyboard support
 * (Arrows/Home/End/Enter/Escape/Tab) plus type-to-jump when not searchable.
 *
 * Props
 *   value, onChange — controlled string
 *   options         — [{ value: string, label: string }]
 *   placeholder     — label shown when value is empty
 *   disabled        — locks the trigger
 *   searchable      — show filter box (auto-on at >=8 options when undef)
 *   hint            — small helper line rendered under the trigger
 *   id, name, ariaLabel, ariaDescribedBy
 *   className       — extra class applied to the wrap
 */
const TYPEAHEAD_RESET_MS = 600;

export default function Select({
  value,
  onChange,
  options = [],
  placeholder = "— Select —",
  disabled = false,
  searchable,
  hint,
  id,
  name,
  ariaLabel,
  ariaDescribedBy,
  className,
}) {
  const generatedId = useId();
  const triggerId = id || `sel-${generatedId}`;
  const listboxId = `${triggerId}-listbox`;

  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const typeBuf = useRef({ text: "", until: 0 });

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const [highlight, setHighlight] = useState(-1);
  const [search, setSearch] = useState("");

  const isSearchable = searchable ?? options.length >= 8;

  // Filter options when searching — case-insensitive substring match.
  // Empty search keeps the original order so admin-style browsing works.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => (o.label || "").toLowerCase().includes(q));
  }, [options, search]);

  const selected = options.find(o => o.value === value) || null;
  const triggerLabel = selected?.label ?? "";

  // ── Positioning ────────────────────────────────────────────────────
  // Same idea as DatePicker: anchor to the trigger's bounding rect,
  // position:fixed so we escape modal overflow. Flip above when the
  // popup would overflow the viewport bottom.
  function recomputeCoords() {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const popH = popupRef.current?.offsetHeight || 280;
    const wouldOverflowBottom = rect.bottom + 8 + popH > window.innerHeight;
    const top = wouldOverflowBottom
      ? Math.max(8, rect.top - 8 - popH)
      : rect.bottom + 8;
    // Right-edge guard so wide popups never hug the viewport edge.
    const popW = Math.max(rect.width, popupRef.current?.offsetWidth || rect.width);
    const overshootRight = (rect.left + popW) - (window.innerWidth - 8);
    const left = Math.max(8, rect.left - Math.max(0, overshootRight));
    setCoords({ top, left, width: rect.width });
  }

  // useLayoutEffect runs after DOM mutation but before paint, so the
  // first frame the popup is visible it already has correct coords.
  useLayoutEffect(() => {
    if (!open) return;
    recomputeCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => recomputeCoords();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Outside-click — mousedown so it fires before any click handler runs.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      closePopup();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open || highlight < 0) return;
    const row = listRef.current?.children?.[highlight];
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open]);

  // If filtering shrinks the list past current highlight, clamp it.
  useEffect(() => {
    if (!open) return;
    if (highlight >= filtered.length) {
      setHighlight(filtered.length ? 0 : -1);
    }
  }, [filtered, open, highlight]);

  function openPopup() {
    if (disabled) return;
    setOpen(true);
    const idx = options.findIndex(o => o.value === value);
    setHighlight(idx >= 0 ? idx : (options.length ? 0 : -1));
    if (isSearchable) {
      // Defer focus to the next microtask so the input has mounted.
      queueMicrotask(() => searchRef.current?.focus());
    }
  }

  function closePopup() {
    setOpen(false);
    setSearch("");
    setHighlight(-1);
    typeBuf.current = { text: "", until: 0 };
  }

  function pick(opt) {
    if (!opt) return;
    onChange?.(opt.value);
    closePopup();
    triggerRef.current?.focus();
  }

  function moveHighlight(dir) {
    if (filtered.length === 0) { setHighlight(-1); return; }
    setHighlight(h => {
      const next = h + dir;
      if (next < 0) return filtered.length - 1;
      if (next >= filtered.length) return 0;
      return next;
    });
  }

  // Shared keydown handler — wired to both the trigger and the search
  // input so behavior is identical regardless of where focus lives.
  function onKeyDown(e) {
    if (disabled) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        openPopup();
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      closePopup();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "Tab") {
      // If focus is inside the portaled popup, return it to the trigger
      // first so the browser's default Tab advances naturally to the
      // next form field. Do NOT preventDefault.
      if (popupRef.current?.contains(e.target)) {
        triggerRef.current?.focus();
      }
      closePopup();
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); moveHighlight(+1); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); moveHighlight(-1); return; }
    if (e.key === "Home")      { e.preventDefault(); setHighlight(0); return; }
    if (e.key === "End")       { e.preventDefault(); setHighlight(filtered.length - 1); return; }
    if (e.key === "Enter")     { e.preventDefault(); pick(filtered[highlight]); return; }

    // Type-to-jump for the non-searchable case. When searchable, the
    // input itself captures keystrokes and filters — don't double-handle.
    if (!isSearchable && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = Date.now();
      const buf = typeBuf.current.until > now
        ? typeBuf.current.text + e.key
        : e.key;
      typeBuf.current = { text: buf, until: now + TYPEAHEAD_RESET_MS };
      const q = buf.toLowerCase();
      const idx = filtered.findIndex(o => (o.label || "").toLowerCase().startsWith(q));
      if (idx >= 0) setHighlight(idx);
    }
  }

  const activeDescId = open && highlight >= 0
    ? `${listboxId}-opt-${highlight}`
    : undefined;

  return (
    <div className={`${s.wrap} ${className || ""}`} ref={wrapRef}>
      <button
        type="button"
        id={triggerId}
        ref={triggerRef}
        className={
          `${s.trigger} ` +
          `${open ? s.triggerOpen : ""} ` +
          `${disabled ? s.triggerDisabled : ""} ` +
          `${!triggerLabel ? s.triggerEmpty : ""}`
        }
        onClick={() => (open ? closePopup() : openPopup())}
        onKeyDown={onKeyDown}
        disabled={disabled}
        name={name}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-activedescendant={activeDescId}
      >
        <span className={s.triggerLabel}>{triggerLabel || placeholder}</span>
        <svg
          className={`${s.chev} ${open ? s.chevOpen : ""}`}
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {hint && <div className={s.hint}>{hint}</div>}

      {open && createPortal(
        <div
          ref={popupRef}
          className={s.popover}
          style={{ top: coords.top, left: coords.left, minWidth: coords.width }}
        >
          {isSearchable && (
            <div className={s.searchRow}>
              <svg
                className={s.searchIcon}
                width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                className={s.search}
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={onKeyDown}
                aria-label="Filter options"
                aria-controls={listboxId}
                aria-activedescendant={activeDescId}
                autoComplete="off"
                spellCheck="false"
              />
            </div>
          )}

          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            className={s.list}
            tabIndex={-1}
          >
            {filtered.length === 0 ? (
              <li className={s.empty} role="presentation">No matches</li>
            ) : (
              filtered.map((opt, i) => {
                const isSelected = opt.value === value;
                const isHighlighted = i === highlight;
                return (
                  <li
                    id={`${listboxId}-opt-${i}`}
                    key={opt.value || `__opt_${i}`}
                    role="option"
                    aria-selected={isSelected}
                    className={
                      `${s.option} ` +
                      `${isSelected ? s.optionSelected : ""} ` +
                      `${isHighlighted ? s.optionHighlighted : ""}`
                    }
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(e) => {
                      // mousedown so the trigger doesn't lose focus to a
                      // click-then-blur cycle before we close.
                      e.preventDefault();
                      pick(opt);
                    }}
                  >
                    <span className={s.optionLabel}>{opt.label}</span>
                    {isSelected && (
                      <svg
                        className={s.checkIcon}
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="3"
                        strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="5 12 10 17 19 7" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
